import { IconBackArrowLeft } from '@holbonkit/icons'
import {
    BottomSheet,
    BottomSheetBody,
    BottomSheetClose,
    BottomSheetContent,
    BottomSheetHeader,
    BottomSheetTitle,
    Box,
    Button,
    Text,
    Title,
} from '@holbonkit/ui'

import { currencyParser } from '@/shared/lib/currency-parser'
import { navigateToDriverAppCity } from '@/shared/lib/navigate'

import styles from './bottom-sheet.module.scss'

import { IllustrationWarning } from '@/assets'

type TWarningBottomSheetProps = {
    isBottomSheetOpen: boolean
    setIsBottomSheetOpen: (isOpen: boolean) => void
    type: 'fns_unavailable' | 'not_self_employed' | 'self_employed_status_lost'
}

export const WarningBottomSheet = ({
    isBottomSheetOpen,
    setIsBottomSheetOpen,
    type,
}: TWarningBottomSheetProps) => {
    const title = () => {
        switch (type) {
            case 'not_self_employed': {
                return 'Оформите самозанятость'
            }
            case 'self_employed_status_lost': {
                return 'Статус самозанятости утерян'
            }
            case 'fns_unavailable': {
                return 'Технические неполадки'
            }
        }
    }

    const description = () => {
        switch (type) {
            case 'not_self_employed': {
                return 'Чтобы выводить деньги по СБП, нужно оформить самозанятость'
            }
            case 'self_employed_status_lost': {
                return 'Чтобы выводить деньги через СБП, восстановите статус самозанятого'
            }
            case 'fns_unavailable': {
                return 'Произошёл сбой у провайдера. Вывод средств временно недоступен'
            }
        }
    }

    const buttonText = () => {
        switch (type) {
            case 'not_self_employed':
            case 'self_employed_status_lost': {
                return 'К профилю самозанятого'
            }
            case 'fns_unavailable': {
                return 'К заказам'
            }
        }
    }

    const handleButtonClick = () => {
        switch (type) {
            case 'not_self_employed':
            case 'self_employed_status_lost': {
                alert('Как нибудь реализуем переход на страницу')
                break
            }
            case 'fns_unavailable': {
                navigateToDriverAppCity()
                break
            }
        }
    }

    return (
        <BottomSheet open={isBottomSheetOpen} onOpenChange={setIsBottomSheetOpen}>
            <BottomSheetContent gripper>
                <BottomSheetBody className={styles['bottom-sheet']}>
                    <img
                        alt='Empty'
                        className={styles['bottom-sheet__image']}
                        src={IllustrationWarning}
                    />
                    <Box className={styles['bottom-sheet__text']}>
                        <Title variant='header-24-bold'>{title()}</Title>
                        <Text variant='body-m-16-medium'>{description()}</Text>
                    </Box>

                    <Button style={{ width: '100%' }} variant='primary' onClick={handleButtonClick}>
                        {buttonText()}
                    </Button>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}

type TСonfirmBottomSheetProps = {
    amount: number
    isBottomSheetOpen: boolean
    isSubmitLoading: boolean
    onSubmit: () => void
    phoneNumber: string
    setIsBottomSheetOpen: (isOpen: boolean) => void
}

export const ConfirmBottomSheet = ({
    isBottomSheetOpen,
    setIsBottomSheetOpen,
    amount,
    phoneNumber,
    onSubmit,
    isSubmitLoading,
}: TСonfirmBottomSheetProps) => {
    return (
        <BottomSheet open={isBottomSheetOpen} onOpenChange={setIsBottomSheetOpen}>
            <BottomSheetContent gripper showCloseButton={false}>
                <BottomSheetHeader>
                    <BottomSheetTitle className={styles['bottom-sheet__title']}>
                        Подтвердите вывод
                    </BottomSheetTitle>
                    <BottomSheetClose>
                        <Box className={styles['bottom-sheet__header-back']}>
                            <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                        </Box>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__body']}>
                    <Text variant='body-m-16-regular'>
                        Выводим всю сумму ({currencyParser(amount)}₽) в Альфа-Банк по номеру{' '}
                        {phoneNumber}
                    </Text>
                    <Box className={styles['bottom-sheet__body--buttons']}>
                        <Button
                            style={{ flex: 1 }}
                            variant='secondary'
                            onClick={() => setIsBottomSheetOpen(false)}
                        >
                            Отмена
                        </Button>
                        <Button
                            isLoading={isSubmitLoading}
                            style={{ flex: 1 }}
                            variant='primary'
                            onClick={onSubmit}
                        >
                            {!isSubmitLoading && 'Вывести'}
                        </Button>
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}

type TAboutBottomSheetProps = {
    isBottomSheetOpen: boolean
    setIsBottomSheetOpen: (isOpen: boolean) => void
}

export const AboutBottomSheet = ({
    isBottomSheetOpen,
    setIsBottomSheetOpen,
}: TAboutBottomSheetProps) => {
    return (
        <BottomSheet open={isBottomSheetOpen} onOpenChange={setIsBottomSheetOpen}>
            <BottomSheetContent>
                <BottomSheetHeader>
                    <BottomSheetTitle>Что это значит?</BottomSheetTitle>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__body']}>
                    <Text variant='body-m-16-regular'>
                        Это сумма комиссии Drivee с учётом НДС
                        <br />
                        за текущий месяц. Она уже списана
                        <br />с заказов — показываем здесь, чтобы всё
                        <br />
                        было наглядно
                    </Text>
                    <Box className={styles['bottom-sheet__body--buttons']}>
                        <Button
                            size='lg'
                            style={{ flex: 1 }}
                            variant='primary'
                            onClick={() => setIsBottomSheetOpen(false)}
                        >
                            Понятно
                        </Button>
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}
